import type {ReactElement} from 'react';
import {useState} from 'react';
import styles from '@Components/social-media-wizard/components/social-media-wizard-view-toggle/social-media-wizard-view-toggle.module.scss';
import {Size} from '@Components/button';
import {ClickableDiv} from '@Components/clickable-div';
import {Text, TextSize} from '@Components/text';
import {noop} from '@Utils/general.util';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';

export interface ViewToggleBtnConfig {
  text: string;
  onClick: VoidFunction;
  showPulsatingDot?: boolean;
}

interface SocialMediaWizardViewToggleProps {
  buttons: ViewToggleBtnConfig[];
  buttonSize?: Size;
  showPulsatingHighlight?: boolean;
}

const defaultViewToggleBtnConfigProp = [
  {
    text: window.i18next.t('pmwjs_content'),
    onClick: noop,
    showPulsatingDot: false,
  },
  {
    text: window.i18next.t('pmwjs_preview'),
    onClick: noop,
    showPulsatingDot: false,
  },
];

export function SocialMediaWizardViewToggle({
  buttons = defaultViewToggleBtnConfigProp,
  buttonSize = Size.SMALL,
  showPulsatingHighlight = false,
}: SocialMediaWizardViewToggleProps): ReactElement<any> {
  const [currentActiveIndex, setCurrentActiveIndex] = useState(0);
  const numberButtons = buttons.length;
  const sliderId = 'button-slider';
  const wrapperId = 'buttons-wrapper';
  const borderThickness = 1;

  const onButtonClick = (clickedIndex: number): void => {
    const parentContainer = document.getElementById(wrapperId);
    const clickedButton = document.getElementById(`toggle-button-${clickedIndex}`);
    const slider = document.getElementById(`button-slider`);

    if (clickedIndex === numberButtons || !clickedButton || !parentContainer || !slider) {
      return;
    }

    buttons[clickedIndex].onClick();
    setCurrentActiveIndex(clickedIndex);

    const parentRect = parentContainer.getBoundingClientRect();
    const childRect = clickedButton.getBoundingClientRect();
    slider.style.left = `${childRect.left - parentRect.left - borderThickness}px`;
  };

  const getSecondaryButtonSize = (): string => {
    if (buttonSize === Size.XSMALL) {
      return `spacing-p-l-3 spacing-p-r-3 ${styles.xsmall}`;
    }

    if (buttonSize === Size.SMALL) {
      return `spacing-p-l-4 spacing-p-r-4 spacing-p-t-2 ${styles.small}`;
    }

    if (buttonSize === Size.MEDIUM) {
      return `spacing-p-l-5 spacing-p-r-5 spacing-p-b-3 ${styles.medium}`;
    }

    return `spacing-p-l-6 spacing-p-r-6 ${styles.large}`;
  };

  const getClassesForPulsatingDot = (): string => {
    if (buttonSize === Size.XSMALL) {
      return styles.xsmallBtn;
    }

    if (buttonSize === Size.SMALL) {
      return styles.smallBtn;
    }

    if (buttonSize === Size.MEDIUM) {
      return styles.mediumBtn;
    }

    return styles.largeBtn;
  };

  const getTextSize = (): TextSize => {
    if (buttonSize === Size.XSMALL) {
      return TextSize.XXSMALL;
    }

    if (buttonSize === Size.SMALL) {
      return TextSize.XSMALL;
    }

    if (buttonSize === Size.MEDIUM) {
      return TextSize.SMALL;
    }

    return TextSize.MEDIUM;
  };

  const getSlider = (buttonEl: ReactElement<any>[]): ReactElement<any>[] => {
    buttonEl.push(
      <ClickableDiv
        key={sliderId}
        id={sliderId}
        onClick={(): void => {
          onButtonClick(numberButtons);
        }}
        className={`${styles.primaryButton} ${styles.slider} ${getSecondaryButtonSize()} flex-column-align-center flex-content-center`}
      >
        <Text val={buttons[currentActiveIndex].text} size={getTextSize()} bold />
      </ClickableDiv>
    );

    return buttonEl;
  };

  const getButtons = (): ReactElement<any>[] => {
    const buttonEls: ReactElement<any>[] = [];

    for (let i = 0; i < buttons.length; i++) {
      const {text, showPulsatingDot} = buttons[i];

      buttonEls.push(
        <ClickableDiv
          id={`toggle-button-${i}`}
          key={`toggle-button-${i}`}
          onClick={(): void => {
            onButtonClick(i);
          }}
          className={`${styles.secondaryButton} ${i !== 0 ? 'spacing-m-l-1' : ''} ${getSecondaryButtonSize()} flex-column-align-center flex-content-center ${showPulsatingDot ? 'spacing-p-r-6' : ''}`}
        >
          {getPulsatingDotForButton(!!showPulsatingDot)}
          <Text val={text} size={getTextSize()} bold className="content-body-white" />
        </ClickableDiv>
      );
    }

    return getSlider(buttonEls);
  };

  const getPulsatingDotForButton = (showPulsatingDot: boolean): ReactElement<any> | null => {
    if (!showPulsatingDot) {
      return null;
    }

    return <PulsatingDot containerClassname={`${styles.pulsatingDot} ${getClassesForPulsatingDot()}`} />;
  };

  const getPulses = (): ReactElement<any> | null => {
    if (!showPulsatingHighlight) {
      return null;
    }

    return (
      <>
        <div className={`${styles.pulse} ${styles.pulse1}`} />
        <div className={`${styles.pulse} ${styles.pulse2}`} />
        <div className={`${styles.pulse} ${styles.pulse3}`} />
      </>
    );
  };

  return (
    <div className={`${styles.highlightPulseContainer} _fit-width`}>
      <div
        id={wrapperId}
        className={`flex-center _fit-height _fit-width spacing-p-1 _position-relative ${styles.toggleViewContainer} ${showPulsatingHighlight ? styles.hideBorder : ''}`}
      >
        {getButtons()}
        {getPulses()}
      </div>
    </div>
  );
}
